import React, { useCallback, useMemo, useRef, useState } from 'react';
import BotItem from '../../ClusterItem/BotItem';
import BotListRow from './BotListRow';
import { useFilter, SortOption } from '../FilterContext';
import { SubscriptionSummary } from '../../../../types/subscription';
import { SortingToggleOptions } from '../../types';
import './index.css';

/**
 * Map the FilterContext SortOption (display string) to the legacy
 * SortingToggleOptions enum used by BotItem's chart logic. The chart
 * picks which data series to render based on this enum.
 */
const sortOptionToToggle = (s: SortOption): SortingToggleOptions => {
  switch (s) {
    case 'Total Stable Profit':
      return SortingToggleOptions.TotalStableProfit;
    case 'Total Asset Profit':
      return SortingToggleOptions.TotalAssetProfit;
    case 'Current Stable Profit':
      return SortingToggleOptions.CurrentStableProfit;
    case 'Current Asset Profit':
      return SortingToggleOptions.CurrentAssetProfit;
    case 'Non-Algo Diff':
      return SortingToggleOptions.NoAlgorithmicDifference;
    case 'Total Value':
    default:
      return SortingToggleOptions.TotalValue;
  }
};

const GridIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const ListIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

interface SubscriptionsSectionProps {
  /** Kept for backwards compat — sortedSubs come from FilterContext. */
  subscriptions?: SubscriptionSummary[];
  totalValue?: number;
}

const SubscriptionsSection: React.FC<SubscriptionsSectionProps> = () => {
  const { view, setView, sortedSubs, query, sort } = useFilter();
  const activeToggleOption = useMemo(() => sortOptionToToggle(sort), [sort]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleViewChange = useCallback(
    (newView: 'grid' | 'list') => {
      const scrollContainer = containerRef.current?.closest(
        '.overview-content-container',
      );
      const scrollTop = scrollContainer?.scrollTop ?? 0;
      setView(newView);
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
        }
      });
    },
    [setView],
  );

  return (
    <div className="subs-container" ref={containerRef}>
      <div className="subs-title-row">
        <h3 className="subs-title">Subscriptions</h3>
        <div className="subs-view-toggle">
          <button
            className={`subs-view-btn${
              view === 'grid' ? ' subs-view-btn--active' : ''
            }`}
            onClick={() => handleViewChange('grid')}
          >
            <GridIcon /> Grid
          </button>
          <button
            className={`subs-view-btn${
              view === 'list' ? ' subs-view-btn--active' : ''
            }`}
            onClick={() => handleViewChange('list')}
          >
            <ListIcon /> List
          </button>
        </div>
      </div>

      {sortedSubs.length === 0 ? (
        <p className="subs-empty">
          No subscriptions match &lsquo;{query}&rsquo;.
        </p>
      ) : view === 'grid' ? (
        <div className="subs-scroll-wrapper">
          <div
            className="subs-grid-list"
            ref={listRef}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              columnGap: 18,
              rowGap: 18,
              alignContent: 'start',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {sortedSubs.map((sub, index) => (
              <div
                key={sub.id}
                style={{ minWidth: 0, minHeight: 0, display: 'flex' }}
              >
                <BotItem
                  subscription={sub}
                  activeToggleOption={activeToggleOption}
                  parentRef={listRef}
                  isModalOpen={isModalOpen}
                  setIsModalOpen={setIsModalOpen}
                  delayIndex={index}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="subs-scroll-wrapper subs-scroll-wrapper--list">
          <div className="subs-list-card">
            <div className="subs-list-header">
              <span></span>
              <span>Pair</span>
              <span>Chart</span>
              <span>Total Value</span>
              <span>Total Stable</span>
              <span>Total Asset</span>
              <span>Current</span>
              <span>Non-Algo</span>
              <span>Quote / Asset</span>
            </div>
            <div className="subs-list-rows">
              {sortedSubs.map((sub, index) => (
                <BotListRow key={sub.id} sub={sub} delayIndex={index} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsSection;
